<i18n>
{
  "en": {
    "list_all": "All Movies",
    "search_results": "Search Results",
    "search_results_series_0": "Series '",
    "search_results_series_1": "'",
    "search_results_fts_0": "Search Results for '",
    "search_results_fts_1": "'",
    "actress_results_0": "'",
    "actress_results_1": "' Appearances",
    "year_results": " Release Year",
    "filter": "Filter",
    "sort_label": "Sort Order",
    "sort_newest": "Newest Movies",
    "sort_oldest": "Oldest Movies",
    "sort_popular": "Popular Movies",
    "movie_status": "Status",
    "movie_type": "Type",
    "movie_year_released": "Year Released"
  },
  "ja": {
    "list_all": "作品一覧",
    "search_results": "検索結果",
    "search_results_series_0": "「",
    "search_results_series_1": "」シリーズ",
    "search_results_fts_0": "「",
    "search_results_fts_1": "」の検索結果",
    "actress_results_0": "「",
    "actress_results_1": "」 出演",
    "year_results": "年配信",
    "filter": "フィルタ",
    "sort_label": "並べ替え",
    "sort_newest": "新着順（新⇒古）",
    "sort_oldest": "配信順（古⇒新）",
    "sort_popular": "よく再生される動画",
    "movie_status": "配信ステータス",
    "movie_type": "動画タイプ",
    "movie_year_released": "配信年"
  }
}
</i18n>

<template>
  <div class="list-title">
    <h1>
      <!-- list header: sort order -->
      <span v-if="$route.name === 'movie-list' && $route.query.o">{{ selectedSortOrder }}</span>
      <!-- list header: default -->
      <span v-else-if="$route.name === 'movie-list'">{{ $t('list_all') }}</span>
      <!-- list header: category results -->
      <span v-else-if="$route.name === 'movie-search' && ($route.query.c) && categoryMap[$route.query.c]">{{ $t('search_results_fts_0') }}{{ categoryMap[$route.query.c].name[locale] }}{{ $t('search_results_fts_1') }}</span>
      <!-- list header: category/type results -->
      <span v-else-if="$route.name === 'movie-search' && ($route.query.mt || $route.query.mc)">{{ $t('search_results') }}</span>
      <!-- list header: text search results -->
      <span v-else-if="$route.name === 'movie-search' && $route.query.s">{{ $t('search_results_fts_0') }}{{ $route.query.s }}{{ $t('search_results_fts_1') }}</span>
      <!-- list header: series list -->
      <span v-else-if="$route.name === 'movie-search' && $route.query.sr && seriesMap[$route.query.sr]">{{ $t('search_results_series_0') }}{{ seriesMap[$route.query.sr].name[locale] }}{{ $t('search_results_series_1') }}</span>
      <!-- list header: actress list -->
      <span v-else-if="$route.name === 'movie-search' && $route.query.a">{{ $t('actress_results_0') }}{{ selectedActress }}{{ $t('actress_results_1') }}</span>
      <!-- list header: year list -->
      <span v-else-if="$route.name === 'movie-search' && $route.query.my">{{ $route.query.my }}{{ $t('year_results') }}</span>

      <!-- list count + current location -->
      <span class="search-count" v-if="locale == 'ja'">({{ totalMovies }}件中 {{ offset + 1 }}-{{ (offset + listSize) > totalMovies ? totalMovies : offset + listSize }}件)</span>
      <span class="search-count" v-if="locale == 'en'">({{ offset + 1 }}-{{ (offset + listSize) > totalMovies ? totalMovies : offset + listSize }} of {{ totalMovies }})</span>
    </h1>
  </div>
</template>

<script>
/* eslint max-len: 0 */

import keyBy from 'lodash/keyBy';
import findIndex from 'lodash/findIndex';
import startCase from 'lodash/startCase';
import APP_CONFIG from '@/appConfig';
import BifrostAPI from '@/assets/js/services/Bifrost/API';

export default {
  props: {
    totalMovies: {
      type: Number,
      required: true,
    },
    offset: {
      type: Number,
      required: true,
    },
    listSize: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      sortType: 'newest',
      categoriesList: [],
      selectedCategories: [],
      categoryMap: {},
      seriesMap: {},
      selectedActress: '',
      selectedSortOrder: '',
      showFilterMenu: false,
    };
  },
  created() {
    this.sortType = this.$route.query.o;

    const bfAPI = new BifrostAPI();

    // get categories info for category queries
    bfAPI.getCategories().then((result) => {
      this.categoryMap = Object.assign({}, APP_CONFIG.categories, result);
    });

    // get selected categories
    if (Object.prototype.hasOwnProperty.call(this.$route.query, 'c')) {
      this.selectedCategories = this.$route.query.c.split(',');
    }

    // get series info for series queries
    if (Object.prototype.hasOwnProperty.call(this.$route.query, 'sr')) {
      bfAPI.getSeries().then((result) => {
        this.seriesList = result;

        // build a series map
        this.seriesMap = keyBy(this.seriesList, 'series_id');
      });
    }

    // get actress info for actress queries
    if (Object.prototype.hasOwnProperty.call(this.$route.query, 'a')) {
      bfAPI.getMovieListActress(0, 1, this.$route.query.a).then((result) => {
        // find index of actress in Actress data (why? because some movies have more than 1 actress)
        const actressIdx = findIndex(result.Rows[0].ActorID, item => item === parseInt(this.$route.query.a, 10));

        // set title - actress names are keyed as 'ActressesJa', and 'ActressesEn', so capitalize 1st letter of locale
        const actressKey = `Actresses${startCase(this.locale)}`;
        this.selectedActress = result.Rows[0][actressKey][actressIdx];
      });
    }

    // get selected sort order display text
    if (Object.prototype.hasOwnProperty.call(this.$route.query, 'o')) {
      switch (this.$route.query.o) {
        case 'newest':
          this.selectedSortOrder = this.$t('sort_newest');
          break;
        case 'oldest':
          this.selectedSortOrder = this.$t('sort_oldest');
          break;
        case 'bob':
          this.selectedSortOrder = this.$t('sort_popular');
          break;
        default:
          this.selectedSortOrder = this.$t('sort_newest');
          break;
      }
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
  },
  methods: {
    submenuToggle() {
      this.showFilterMenu = !this.showFilterMenu;
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
